/**
 * F18 — Panel "Plan de pagos" (hitos de facturación parcial).
 * Reutilizable en la ficha de expediente (clientId + caseId) y en la ficha
 * de presupuesto (clientId + quoteId). Los hitos pendientes viven también
 * como eventos HITO_PAGO en el calendario.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  AlertCircle,
  CalendarClock,
  Check,
  Loader2,
  Pencil,
  Plus,
  Receipt,
  Trash2,
  X,
} from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import { formatCents, parseEurosToCents, centsToEurosInput } from '../../lib/money'
import Modal from '../ui/Modal'
import {
  MilestoneStatusLabel,
  type MilestoneDto,
  type MilestoneItemInput,
  type MilestoneListResponse,
} from '@toga/shared'

const inputCls =
  'w-full px-2 py-1 rounded bg-slate-900/40 border border-base text-sm t-primary placeholder:t-faint focus:outline-none focus:border-toga-500/50'

interface Props {
  clientId: string
  caseId?: string
  quoteId?: string
  canWrite: boolean
}

export default function MilestonesPanel({ clientId, caseId, quoteId, canWrite }: Props) {
  const navigate = useNavigate()
  const [data, setData] = useState<MilestoneListResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  // Form de alta/edición de un hito.
  const [fTitle, setFTitle] = useState('')
  const [fAmountEur, setFAmountEur] = useState('')
  const [fVatPct, setFVatPct] = useState('21')
  const [fDate, setFDate] = useState('')

  const load = useCallback(async () => {
    setError(null)
    try {
      const params = new URLSearchParams()
      if (quoteId) params.set('quoteId', quoteId)
      else if (caseId) params.set('caseId', caseId)
      else params.set('clientId', clientId)
      setData(await api.get<MilestoneListResponse>(`/milestones?${params.toString()}`))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar')
    }
  }, [clientId, caseId, quoteId])

  useEffect(() => {
    void load()
  }, [load])

  const pending = useMemo(
    () => (data?.items ?? []).filter((m) => m.status === 'PENDIENTE'),
    [data]
  )

  const toggle = (id: string) => {
    setSelected((s) => {
      const next = new Set(s)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const resetForm = () => {
    setFTitle('')
    setFAmountEur('')
    setFVatPct('21')
    setFDate('')
    setAdding(false)
    setEditingId(null)
  }

  const startEdit = (m: MilestoneDto) => {
    setEditingId(m.id)
    setAdding(false)
    setFTitle(m.title)
    setFAmountEur(centsToEurosInput(m.amountCents))
    setFVatPct((Number(m.vatRate) * 100).toString())
    setFDate(m.dueDate.slice(0, 10))
  }

  const submitForm = async () => {
    if (!fTitle.trim() || !fDate || parseEurosToCents(fAmountEur) <= 0) {
      setError('Indica título, importe y fecha del hito')
      return
    }
    setBusy(true)
    setError(null)
    try {
      if (editingId) {
        await api.patch(`/milestones/${editingId}`, {
          title: fTitle.trim(),
          amountCents: parseEurosToCents(fAmountEur),
          vatRate: String((Number(fVatPct) || 0) / 100),
          dueDate: new Date(`${fDate}T12:00:00`).toISOString(),
        })
      } else {
        await api.post('/milestones', {
          clientId,
          caseId: caseId ?? null,
          quoteId: quoteId ?? null,
          items: [
            {
              position: data?.items.length ?? 0,
              title: fTitle.trim(),
              amountCents: parseEurosToCents(fAmountEur),
              vatRate: String((Number(fVatPct) || 0) / 100),
              dueDate: new Date(`${fDate}T12:00:00`).toISOString(),
            },
          ],
        })
      }
      resetForm()
      await load()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'Error'))
    } finally {
      setBusy(false)
    }
  }

  const onDelete = async (id: string) => {
    if (!window.confirm('¿Eliminar este hito? Se retirará también del calendario.')) return
    setBusy(true)
    try {
      await api.delete(`/milestones/${id}`)
      setSelected((s) => {
        const next = new Set(s)
        next.delete(id)
        return next
      })
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error')
    } finally {
      setBusy(false)
    }
  }

  const onInvoiceSelection = async () => {
    setBusy(true)
    setError(null)
    try {
      const { invoiceId } = await api.post<{ invoiceId: string }>('/milestones/invoice', {
        ids: [...selected],
      })
      navigate(`/billing/${invoiceId}`)
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'Error'))
      setBusy(false)
    }
  }

  const items = data?.items ?? []

  return (
    <div className="surface p-4 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="t-primary font-semibold text-sm flex items-center gap-2">
          <CalendarClock size={15} className="text-teal-300" />
          Plan de pagos
          {data && data.pendingAmountCents > 0 && (
            <span className="text-xs t-muted font-normal">
              · pendiente {formatCents(data.pendingAmountCents)} (base)
            </span>
          )}
        </h3>
        <div className="flex items-center gap-2">
          {canWrite && selected.size > 0 && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void onInvoiceSelection()}
              className="px-2.5 py-1.5 rounded-lg text-xs font-semibold text-white bg-gradient-to-r from-toga-600 to-toga-700 hover:shadow-glow-toga flex items-center gap-1.5"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Receipt size={13} />}
              Facturar selección ({selected.size})
            </button>
          )}
          {canWrite && !adding && !editingId && (
            <button
              type="button"
              onClick={() => {
                resetForm()
                setAdding(true)
              }}
              className="px-2.5 py-1.5 rounded-lg text-xs border border-base hover:bg-hover t-secondary flex items-center gap-1.5"
            >
              <Plus size={13} /> Añadir hito
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-2.5 py-1.5 flex items-center gap-2">
          <AlertCircle size={12} /> {error}
        </div>
      )}

      {items.length === 0 && !adding && (
        <p className="text-xs t-faint">
          Sin hitos. Divide importes grandes en hitos de pago: cada uno aparece en el calendario y
          se factura por separado.
        </p>
      )}

      {(items.length > 0 || adding) && (
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider t-faint">
            <tr>
              {canWrite && <th className="w-7" />}
              <th className="text-left py-1">Hito</th>
              <th className="text-left w-28">Fecha</th>
              <th className="text-right w-28">Base</th>
              <th className="text-right w-16">IVA %</th>
              <th className="text-left w-28">Estado</th>
              {canWrite && <th className="w-16" />}
            </tr>
          </thead>
          <tbody>
            {items.map((m) =>
              editingId === m.id ? (
                <FormRow
                  key={m.id}
                  canSelect={canWrite}
                  fTitle={fTitle}
                  fAmountEur={fAmountEur}
                  fVatPct={fVatPct}
                  fDate={fDate}
                  busy={busy}
                  setFTitle={setFTitle}
                  setFAmountEur={setFAmountEur}
                  setFVatPct={setFVatPct}
                  setFDate={setFDate}
                  onSubmit={() => void submitForm()}
                  onCancel={resetForm}
                />
              ) : (
                <tr key={m.id} className="border-t border-base/40">
                  {canWrite && (
                    <td className="py-1.5">
                      {m.status === 'PENDIENTE' && (
                        <input
                          type="checkbox"
                          checked={selected.has(m.id)}
                          onChange={() => toggle(m.id)}
                        />
                      )}
                    </td>
                  )}
                  <td className="py-1.5 t-primary">
                    {m.title}
                    {m.description && (
                      <span className="block text-xs t-faint truncate max-w-[280px]">
                        {m.description}
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 text-xs t-secondary">
                    {new Date(m.dueDate).toLocaleDateString('es-ES')}
                  </td>
                  <td className="py-1.5 text-right font-mono t-secondary">
                    {formatCents(m.amountCents)}
                  </td>
                  <td className="py-1.5 text-right text-xs t-muted">
                    {(Number(m.vatRate) * 100).toFixed(0)}
                  </td>
                  <td className="py-1.5 text-xs">
                    {m.status === 'FACTURADO' && m.invoiceId ? (
                      <Link
                        to={`/billing/${m.invoiceId}`}
                        className="text-toga-300 hover:underline"
                      >
                        {m.invoiceNumber ?? 'Factura borrador'}
                      </Link>
                    ) : (
                      <span
                        className={
                          m.status === 'PENDIENTE' ? 'text-teal-300' : 't-muted'
                        }
                      >
                        {MilestoneStatusLabel[m.status]}
                      </span>
                    )}
                  </td>
                  {canWrite && (
                    <td className="py-1.5 text-right whitespace-nowrap">
                      {m.status === 'PENDIENTE' && (
                        <>
                          <button
                            type="button"
                            onClick={() => startEdit(m)}
                            className="t-faint hover:text-toga-300 mr-2"
                            title="Editar hito"
                          >
                            <Pencil size={13} />
                          </button>
                          <button
                            type="button"
                            onClick={() => void onDelete(m.id)}
                            className="t-faint hover:text-rose-300"
                            title="Eliminar hito"
                          >
                            <Trash2 size={13} />
                          </button>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              )
            )}
            {adding && (
              <FormRow
                canSelect={canWrite}
                fTitle={fTitle}
                fAmountEur={fAmountEur}
                fVatPct={fVatPct}
                fDate={fDate}
                busy={busy}
                setFTitle={setFTitle}
                setFAmountEur={setFAmountEur}
                setFVatPct={setFVatPct}
                setFDate={setFDate}
                onSubmit={() => void submitForm()}
                onCancel={resetForm}
              />
            )}
          </tbody>
        </table>
      )}
    </div>
  )
}

function FormRow(props: {
  canSelect: boolean
  fTitle: string
  fAmountEur: string
  fVatPct: string
  fDate: string
  busy: boolean
  setFTitle: (v: string) => void
  setFAmountEur: (v: string) => void
  setFVatPct: (v: string) => void
  setFDate: (v: string) => void
  onSubmit: () => void
  onCancel: () => void
}) {
  return (
    <tr className="border-t border-base/40 bg-slate-800/20">
      {props.canSelect && <td />}
      <td className="py-1.5 pr-2">
        <input
          value={props.fTitle}
          onChange={(e) => props.setFTitle(e.target.value)}
          placeholder="P. ej. Hito 2 — Presentación de demanda"
          className={inputCls}
          autoFocus
        />
      </td>
      <td className="py-1.5 pr-2">
        <input
          type="date"
          value={props.fDate}
          onChange={(e) => props.setFDate(e.target.value)}
          className={inputCls}
        />
      </td>
      <td className="py-1.5 pr-2">
        <input
          value={props.fAmountEur}
          onChange={(e) => props.setFAmountEur(e.target.value)}
          placeholder="0,00"
          className={`${inputCls} text-right`}
        />
      </td>
      <td className="py-1.5 pr-2">
        <input
          value={props.fVatPct}
          onChange={(e) => props.setFVatPct(e.target.value)}
          className={`${inputCls} text-right`}
        />
      </td>
      <td colSpan={2} className="py-1.5 whitespace-nowrap">
        <button
          type="button"
          disabled={props.busy}
          onClick={props.onSubmit}
          className="px-2 py-1 rounded text-xs border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 mr-1.5"
        >
          {props.busy ? <Loader2 size={12} className="animate-spin inline" /> : <Check size={12} className="inline" />}
        </button>
        <button
          type="button"
          onClick={props.onCancel}
          className="px-2 py-1 rounded text-xs border border-base t-muted hover:bg-hover"
        >
          <X size={12} className="inline" />
        </button>
      </td>
    </tr>
  )
}

/**
 * Modal "Generar plan de pagos" desde un presupuesto: reparte la base
 * imponible en N hitos mensuales editables y los crea de una vez.
 */
export function PaymentPlanModal({
  open,
  onClose,
  clientId,
  caseId,
  quoteId,
  subtotalCents,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  clientId: string
  caseId: string | null
  quoteId: string
  subtotalCents: number
  onCreated: () => void
}) {
  const [rows, setRows] = useState<Array<{ title: string; amountEur: string; date: string }>>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const buildRows = useCallback(
    (n: number) => {
      const base = Math.floor(subtotalCents / n)
      const remainder = subtotalCents - base * n
      const out: Array<{ title: string; amountEur: string; date: string }> = []
      for (let i = 0; i < n; i++) {
        const d = new Date()
        d.setMonth(d.getMonth() + i)
        out.push({
          title: `Hito ${i + 1} de ${n}`,
          amountEur: centsToEurosInput(base + (i === n - 1 ? remainder : 0)),
          date: d.toISOString().slice(0, 10),
        })
      }
      setRows(out)
    },
    [subtotalCents]
  )

  useEffect(() => {
    if (open) buildRows(3)
  }, [open, buildRows])

  const sumCents = rows.reduce((acc, r) => acc + parseEurosToCents(r.amountEur), 0)

  const onSubmit = async () => {
    setBusy(true)
    setError(null)
    try {
      const items: MilestoneItemInput[] = rows.map((r, i) => ({
        position: i,
        title: r.title.trim() || `Hito ${i + 1}`,
        amountCents: parseEurosToCents(r.amountEur),
        vatRate: '0.21',
        dueDate: new Date(`${r.date}T12:00:00`).toISOString(),
      }))
      await api.post('/milestones', { clientId, caseId, quoteId, items })
      onCreated()
      onClose()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'Error'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Generar plan de pagos">
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm t-secondary">
          Repartir {formatCents(subtotalCents)} (base imponible) en
          <select
            onChange={(e) => buildRows(Number(e.target.value))}
            defaultValue="3"
            className="bg-transparent border border-base rounded px-2 py-1 text-sm"
          >
            {[2, 3, 4, 5, 6, 8, 10, 12].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          hitos
        </div>

        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider t-faint">
            <tr>
              <th className="text-left py-1">Hito</th>
              <th className="text-left w-32">Fecha</th>
              <th className="text-right w-28">Base (€)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-base/40">
                <td className="py-1 pr-2">
                  <input
                    value={r.title}
                    onChange={(e) =>
                      setRows((rs) => rs.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))
                    }
                    className={inputCls}
                  />
                </td>
                <td className="py-1 pr-2">
                  <input
                    type="date"
                    value={r.date}
                    onChange={(e) =>
                      setRows((rs) => rs.map((x, j) => (j === i ? { ...x, date: e.target.value } : x)))
                    }
                    className={inputCls}
                  />
                </td>
                <td className="py-1">
                  <input
                    value={r.amountEur}
                    onChange={(e) =>
                      setRows((rs) =>
                        rs.map((x, j) => (j === i ? { ...x, amountEur: e.target.value } : x))
                      )
                    }
                    className={`${inputCls} text-right`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <p className={`text-xs ${sumCents === subtotalCents ? 't-faint' : 'text-amber-300'}`}>
          Suma del plan: {formatCents(sumCents)}
          {sumCents !== subtotalCents &&
            ` (difiere de la base del presupuesto, ${formatCents(subtotalCents)})`}
        </p>

        {error && (
          <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-2.5 py-1.5 flex items-center gap-2">
            <AlertCircle size={12} /> {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm border border-base hover:bg-hover t-secondary"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={busy || rows.length === 0}
            onClick={() => void onSubmit()}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 to-toga-700 hover:shadow-glow-toga flex items-center gap-1.5"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <CalendarClock size={14} />}
            Crear plan
          </button>
        </div>
      </div>
    </Modal>
  )
}
